import { useState, useEffect } from 'react';
import { useSettingsStore } from '@store/settingsStore';
import { useStoryStore } from '@store/storyStore';

interface TypewriterTextProps {
  text: string;
  onComplete?: () => void;
}

/**
 * TypewriterText component
 * Displays text with a typewriter animation effect
 */
export function TypewriterText({ text, onComplete }: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const textSpeed = useSettingsStore((state) => state.textSpeed);
  const showTypewriter = useSettingsStore((state) => state.showTypewriter);
  const isTypewriterComplete = useStoryStore((state) => state.isTypewriterComplete);
  const setTypewriterComplete = useStoryStore((state) => state.setTypewriterComplete);

  // Calculate delay based on text speed (0-100)
  // Lower speed = longer delay
  const delay = showTypewriter ? Math.max(10, 100 - textSpeed) : 0;

  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
    setTypewriterComplete(false);
  }, [text, setTypewriterComplete]);

  // Skip typewriter effect if user clicked
  useEffect(() => {
    if (isTypewriterComplete && !isComplete) {
      setDisplayedText(text);
      setCurrentIndex(text.length);
      setIsComplete(true);
    }
  }, [isTypewriterComplete, isComplete, text]);

  // Typewriter effect
  useEffect(() => {
    if (isComplete || currentIndex >= text.length) {
      if (!isComplete) {
        setIsComplete(true);
        setTypewriterComplete(true);
        onComplete?.();
      }
      return;
    }

    if (!showTypewriter) {
      // Instant display if typewriter is disabled
      setDisplayedText(text);
      setCurrentIndex(text.length);
      setIsComplete(true);
      setTypewriterComplete(true);
      onComplete?.();
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayedText((prev) => prev + text[currentIndex]);
      setCurrentIndex((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timeout);
  }, [currentIndex, text, delay, isComplete, showTypewriter, onComplete, setTypewriterComplete]);

  return (
    <p className="typewriter-text leading-relaxed text-white whitespace-normal break-words" style={{ textShadow: 'none' }}>
      {displayedText}
      {!isComplete && showTypewriter && (
        <span className="typewriter-cursor animate-pulse text-white ml-0.5">|</span>
      )}
    </p>
  );
}
